import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import {
    Grid,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';

const ReportsAssetInventory = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [groupId, setGroupId] = useState(0);
    const [deviceTypeId, setDeviceTypeId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [deviceTypesData, setDeviceTypesData] = useState([]);
    const [assetInventoryData, setAssetInventoryData] = useState([]);
    const [filtersData, setFiltersData] = useState([]);

    useEffect(() => {
        getGroups();
        getDeviceTypes();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getDeviceTypes = () => {
        ApiHelper.get('/api/reports/device_types/')
            .then((res) => {
                setDeviceTypesData(res.data);
            })
            .catch();
    };

    const getAssetInventoryData = () => {
        setLoading(true);
        ApiHelper.post('/api/reports/asset_inventory/', {
            group_id: groupId === 0 ? '' : groupId.toString(),
            device_type_id: deviceTypeId === 0 ? '' : deviceTypeId.toString()
        })
            .then((res) => {
                setAssetInventoryData(res.data);
                setLoading(false);
                let groupName = 'Any';
                let deviceTypeName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < deviceTypesData.length; i++) {
                    if (deviceTypesData[i].id === deviceTypeId) {
                        deviceTypeName = deviceTypesData[i].name;
                    }
                }
                const temp = [
                    { label: 'Group Name', value: groupName },
                    { label: 'Device Type', value: deviceTypeName }
                ];
                setFiltersData(temp);
            })
            .catch(() => {
                setAssetInventoryData([]);
                toast.warning('There is no asset inventory data');
                setLoading(false);
            });
    };

    return (
        <MainCard title="Operational Reports Management" cardHeaderTitle="Asset Inventory" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                {assetInventoryData.length > 0 && (
                    <Grid item xs={12}>
                        <ReportFileExports
                            elementId="assetInventory"
                            reportTitle="Asset Inventory"
                            downloadFileName="asset_inventory"
                            downloadFileOption="all"
                            data={assetInventoryData}
                            headers={[
                                { label: 'MAC Address', key: 'mac_address' },
                                { label: 'IP Address', key: 'ip_address' },
                                { label: 'Device Name', key: 'device_name' },
                                { label: 'OS Version', key: 'os_version' },
                                { label: 'OS Name', key: 'os_name' },
                                { label: 'Processor', key: 'processor' },
                                { label: 'System', key: 'system' },
                                { label: 'Display 1', key: 'display_1' },
                                { label: 'Display 2', key: 'display_2' },
                                { label: 'Manufacturer', key: 'manufacturer' },
                                { label: 'Device Version', key: 'device_version' },
                                { label: 'Username', key: 'username' },
                                { label: 'Group', key: 'group' },
                                { label: 'Policy', key: 'policy' }
                            ]}
                            filtersData={filtersData}
                        />
                    </Grid>
                )}
                <Grid item xs={2}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Device Type</InputLabel>
                                    <Select
                                        label="Select Device Type"
                                        value={deviceTypeId}
                                        name="device_type"
                                        onChange={(e) => setDeviceTypeId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {deviceTypesData.length > 0 &&
                                            deviceTypesData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getAssetInventoryData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={10}>
                    <Paper sx={{ overflowX: 'auto' }} id="assetInventory">
                        {loading === true ? (
                            <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                Please wait...
                            </Typography>
                        ) : assetInventoryData.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                No data for the asset inventory report
                            </Typography>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="10%">MAC Address</TableCell>
                                        <TableCell width="10%">IP Address</TableCell>
                                        <TableCell width="10%">Device Name</TableCell>
                                        <TableCell width="10%">OS Version</TableCell>
                                        <TableCell width="10%">OS Name</TableCell>
                                        <TableCell width="30%">Processor</TableCell>
                                        <TableCell width="10%">System</TableCell>
                                        <TableCell width="10%">Display 1</TableCell>
                                        <TableCell width="10%">Display 2</TableCell>
                                        <TableCell width="10%">Manufacturer</TableCell>
                                        <TableCell width="10%">Device Version</TableCell>
                                        <TableCell width="10%">Username</TableCell>
                                        <TableCell width="10%">Group</TableCell>
                                        <TableCell width="10%">Policy</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assetInventoryData.map((data, key) => (
                                        <TableRow key={key}>
                                            <TableCell>{data.mac_address}</TableCell>
                                            <TableCell>{data.ip_address}</TableCell>
                                            <TableCell>{data.device_name}</TableCell>
                                            <TableCell>{data.os_version}</TableCell>
                                            <TableCell>{data.os_name}</TableCell>
                                            <TableCell>{data.processor}</TableCell>
                                            <TableCell>{data.system}</TableCell>
                                            <TableCell>{data.display_1}</TableCell>
                                            <TableCell>{data.display_2}</TableCell>
                                            <TableCell>{data.manufacturer}</TableCell>
                                            <TableCell>{data.device_version}</TableCell>
                                            <TableCell>{data.username}</TableCell>
                                            <TableCell>{data.group}</TableCell>
                                            <TableCell>{data.policy}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ReportsAssetInventory;
